import { useEffect, useRef, useState } from "react";
import { ContactDetails } from "../ContactDetails/ContactDetails";
import { ProviderInfo, deepEqual, isFormEmpty, validate } from "../utils";
import classes from "./ProviderForm.module.css";
import { FormData } from "../utils";
import { useAddProviderData } from "../../Mutations";
import { useMultiStepForm } from "../Provider/MultiStepFormContext";
import { useNavigate } from "react-router-dom";
import { ErrorPopup } from "../Error/ErrorPopup";
import { LoaderOverlay } from "../Loader/LoadingOverlay";
import { t } from "i18next";

const defaultForm: FormData = {
  dealerName: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  address: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "",
    zipcode: "",
  },
  preferredContact: undefined,
};
export const ProviderForm = () => {
  const navigate = useNavigate();
  const isDirty = useRef(false);
  const addProviderInfo = useAddProviderData();
  const [loading, setLoading] = useState(false);
  const [displayErrorPopup, setDisplayErrorPopup] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [form, setForm] = useState<FormData>(defaultForm);
  const { providerFormData, clearFormData } = useMultiStepForm();
  const selectedCountry = sessionStorage.getItem("country") ?? "";

  // Detect form changes
  useEffect(() => {
    isDirty.current = !deepEqual(form, defaultForm);
  }, [form]);

  // ✅ Add browser unload blocker
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleSubmit = async () => {
    const isValid = validate(form, setErrors);
    if (!isValid) {
      // alert(t("form.alert_error"));
      return;
    }
    const customerInfo: ProviderInfo = {
      consigneeId: providerFormData.consigneeId,
      noConsigneeId: providerFormData.noConsigneeId,
      dealerAcknowlegement: providerFormData.dealerAcknowlegement,
      informUserMyself: providerFormData.informUserMyself,
      providePermobilData: providerFormData.providePermobilData,
      dealerName: form.dealerName,
      firstName: form.firstName,
      lastName: form.lastName,
      phone: form.phone,
      email: form.email,
      line1: form.address.line1 || "Not available",
      line2: form.address.line2 || "Not available",
      city: form.address.city || "Not available",
      state: form.address.state || "Not available",
      country: form.address.country || "Not available",
      zipcode: form.address.zipcode || "Not available",
      preferredContactType: form.preferredContact ?? "",
      selectedCountry: selectedCountry,
    };
    setLoading(true);
    try {
      const result = await addProviderInfo.mutateAsync(customerInfo);
      if (result.status === 200) {
        clearFormData();
        navigate("/confirmation");
      } else {
        setDisplayErrorPopup(true);
      }
    } catch (error) {
      setDisplayErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };
  const handleBack = () => {
    navigate("/consent");
  };

  return (
    <div className={classes.container}>
      <h2 className={classes.title}>{t("form.page_title")}</h2>
      <ContactDetails form={form} setForm={setForm} errors={errors} />
      <div className={"mandatoryText"}>* {t("common.required")}</div>
      <div className={classes.nextContainer}>
        <button className={classes.backButton} onClick={handleBack}>
          {t("common.back")}
        </button>
        <button
          onClick={handleSubmit}
          className={classes.nextButton}
          disabled={isFormEmpty(form)}
        >
          {t("common.submit")}
        </button>
      </div>
      {displayErrorPopup && <ErrorPopup />}
      {loading && <LoaderOverlay />}
    </div>
  );
};
